// src/pages/Agenda.js — Fusión: Tabs documentos + CRM Citas
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import Swal from "sweetalert2";
import { generarPDF } from "../utils/generarPDF";
import { generarRemisionPDF as generarRemision } from "../utils/generarRemision";
import Protegido from "../components/Protegido";
import "../estilos/AgendaModerna.css";
import { useNavigationState } from "../context/NavigationContext";
import { saldoPedido, estaPagado as pedidoPagado } from "../utils/saldoPedido";

// ════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════
const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;

const formatoFecha = (f) => {
  if (!f) return "";
  return new Date(f + "T12:00:00").toLocaleDateString("es-CO", { day: "numeric", month: "short" });
};

const ESTADOS_CITA = {
  programada:  { label: "Programada",  color: "#f59e0b", bg: "#fefce8", border: "#fde68a", emoji: "🟡" },
  confirmada:  { label: "Confirmada",  color: "#10b981", bg: "#ecfdf5", border: "#a7f3d0", emoji: "🟢" },
  completada:  { label: "Completada",  color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", emoji: "🔵" },
  no_asistio:  { label: "No asistió",  color: "#ef4444", bg: "#fef2f2", border: "#fecaca", emoji: "🔴" },
  cancelada:   { label: "Cancelada",   color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", emoji: "⚫" },
};

// ════════════════════════════════════════
// ICONO DE PAGO (pedidos)
// ════════════════════════════════════════
const IconoPago = ({ orden }) => {
  const saldo = saldoPedido(orden);
  const estaPagado = pedidoPagado(orden);
  return (
    <div
      title={estaPagado ? "Pagado" : `Saldo: $${saldo.toLocaleString()}`}
      style={{
        width: 24, height: 24, borderRadius: "50%",
        backgroundColor: estaPagado ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
        border: `1.5px solid ${estaPagado ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, fontWeight: "bold",
        color: estaPagado ? "rgba(22,163,74,0.85)" : "rgba(220,38,38,0.85)",
        cursor: "default", flexShrink: 0,
      }}
    >$</div>
  );
};

// ════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════
export default function Agenda() {
  const { saveModuleState, getModuleState } = useNavigationState();
  const navigate = useNavigate();
  const estadoGuardado = getModuleState("/agenda");

  // ── Estados existentes ──
  const [fechaSeleccionada, setFechaSeleccionada] = useState(() => {
    if (estadoGuardado?.fechaSeleccionada) return new Date(estadoGuardado.fechaSeleccionada);
    return new Date();
  });
  const [nuevaNota, setNuevaNota] = useState("");
  const [notas, setNotas] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [eventosDelMes, setEventosDelMes] = useState({ ordenes: {}, cotizaciones: {}, citas: {} });

  // ── Tabs para documentos ──
  const [tabDocumentos, setTabDocumentos] = useState("pedidos");

  // ── Citas CRM ──
  const [citas, setCitas] = useState([]);
  const [modalCita, setModalCita] = useState(false);
  const [citaEditarId, setCitaEditarId] = useState(null);
  const [formCita, setFormCita] = useState({ cliente_id: null, hora: "09:00", estado: "programada", motivo: "", notas: "" });
  const [buscarClienteCita, setBuscarClienteCita] = useState("");
  const [clientesSugerencias, setClientesSugerencias] = useState([]);
  const [clienteSelCita, setClienteSelCita] = useState(null);
  const buscandoRef = useRef(false);

  // ── Guardar estado de navegación ──
  const handleFechaChange = (fecha) => {
    setFechaSeleccionada(fecha);
    saveModuleState("/agenda", { fechaSeleccionada: fecha.toISOString() });
  };

  // ════════════════════════════════════════
  // CARGAR DATOS DEL DÍA
  // ════════════════════════════════════════
  const cargarDatos = useCallback(async () => {
    const fecha = fechaSeleccionada.toISOString().split("T")[0];

    const [{ data: notasData }, { data: ordenesData }, { data: cotizacionesData }, { data: citasData }] = await Promise.all([
      supabase.from("agenda").select("*").eq("fecha", fecha).order("created_at", { ascending: true }),
      supabase.from("ordenes_pedido").select("*, clientes(*)").eq("fecha_evento", fecha),
      supabase.from("cotizaciones").select("*, clientes(*)").eq("fecha_evento", fecha),
      supabase.from("citas").select("*, clientes(*)").eq("fecha", fecha).order("hora", { ascending: true }),
    ]);

    setNotas(notasData || []);
    setOrdenes(ordenesData || []);
    setCotizaciones(cotizacionesData || []);
    setCitas(citasData || []);
  }, [fechaSeleccionada]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // ════════════════════════════════════════
  // EVENTOS DEL MES (puntos en calendario)
  // ════════════════════════════════════════
  const cargarEventosDelMes = useCallback(async (activeDate) => {
    const y = activeDate.getFullYear();
    const m = activeDate.getMonth();
    const desde = `${y}-${String(m + 1).padStart(2, "0")}-01`;
    const hasta = `${y}-${String(m + 1).padStart(2, "0")}-${new Date(y, m + 1, 0).getDate()}`;

    const [{ data: ops }, { data: cots }, { data: cts }] = await Promise.all([
      supabase.from("ordenes_pedido").select("fecha_evento").gte("fecha_evento", desde).lte("fecha_evento", hasta),
      supabase.from("cotizaciones").select("fecha_evento").gte("fecha_evento", desde).lte("fecha_evento", hasta),
      supabase.from("citas").select("fecha").gte("fecha", desde).lte("fecha", hasta),
    ]);

    const mapaOps = {};
    (ops || []).forEach((o) => { if (o.fecha_evento) mapaOps[o.fecha_evento.split("T")[0]] = true; });
    const mapaCots = {};
    (cots || []).forEach((c) => { if (c.fecha_evento) mapaCots[c.fecha_evento.split("T")[0]] = true; });
    const mapaCitas = {};
    (cts || []).forEach((c) => { if (c.fecha) mapaCitas[c.fecha.split("T")[0]] = true; });

    setEventosDelMes({ ordenes: mapaOps, cotizaciones: mapaCots, citas: mapaCitas });
  }, []);

  useEffect(() => { cargarEventosDelMes(fechaSeleccionada); }, [cargarEventosDelMes, fechaSeleccionada]);

  // ── Puntos de color en calendario ──
  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const tieneOP = eventosDelMes.ordenes[key];
    const tieneCot = eventosDelMes.cotizaciones[key];
    const tieneCita = eventosDelMes.citas[key];
    if (!tieneOP && !tieneCot && !tieneCita) return null;
    return (
      <div style={{ display: "flex", gap: 3, justifyContent: "center", marginTop: 2 }}>
        {tieneOP && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#0077B6" }} />}
        {tieneCot && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f59e0b" }} />}
        {tieneCita && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#8b5cf6" }} />}
      </div>
    );
  };

  // ════════════════════════════════════════
  // NOTAS
  // ════════════════════════════════════════
  const guardarNota = async () => {
    if (!nuevaNota.trim()) return;
    const fecha = fechaSeleccionada.toISOString().split("T")[0];
    const { error } = await supabase.from("agenda").insert([{ titulo: "Nota", descripcion: nuevaNota, fecha, documento: null, tipo: null }]);
    if (!error) { setNuevaNota(""); cargarDatos(); Swal.fire({ icon: "success", title: "Nota guardada", timer: 1200, showConfirmButton: false }); }
    else Swal.fire("Error", "No se pudo guardar la nota", "error");
  };

  const borrarNota = async (id) => {
    const { error } = await supabase.from("agenda").delete().eq("id", id);
    if (!error) { cargarDatos(); Swal.fire({ icon: "success", title: "Nota eliminada", timer: 1200, showConfirmButton: false }); }
  };

  // ════════════════════════════════════════
  // PEDIDOS — Acciones
  // ════════════════════════════════════════
  const editarOrden = (orden) => {
    const cliente = orden.clientes || {};
    navigate("/crear-documento", {
      state: {
        documento: {
          ...orden, nombre_cliente: cliente.nombre || "", identificacion: cliente.identificacion || "",
          telefono: cliente.telefono || "", direccion: cliente.direccion || "", email: cliente.email || "",
          fecha_creacion: orden.fecha_creacion || orden.fecha || null, abonos: orden.abonos || [],
          garantia: orden.garantia || "", fechaGarantia: orden.fechaGarantia || "",
          garantiaRecibida: orden.garantiaRecibida || false, estado: orden.estado || "",
          numero: orden.numero || "", esEdicion: true, idOriginal: orden.id,
        },
        tipo: "ordenes_pedido",
      },
    });
  };

  const manejarPDF = async (orden) => {
    const doc = { ...orden, nombre_cliente: orden.clientes?.nombre || "No disponible", identificacion: orden.clientes?.identificacion || "-", telefono: orden.clientes?.telefono || "-", direccion: orden.clientes?.direccion || "-", email: orden.clientes?.email || "-" };
    await generarPDF(doc, "orden");
  };

  const manejarRemision = async (orden) => {
    const doc = { ...orden, nombre_cliente: orden.clientes?.nombre || "No disponible", identificacion: orden.clientes?.identificacion || "-", telefono: orden.clientes?.telefono || "-", direccion: orden.clientes?.direccion || "-", email: orden.clientes?.email || "-" };
    await generarRemision(doc);
  };

  // ════════════════════════════════════════
  // COTIZACIONES — Acciones
  // ════════════════════════════════════════
  const editarCotizacion = (cot) => {
    const cliente = cot.clientes || {};
    navigate("/crear-documento", {
      state: {
        documento: {
          ...cot, nombre_cliente: cliente.nombre || "", identificacion: cliente.identificacion || "",
          telefono: cliente.telefono || "", direccion: cliente.direccion || "", email: cliente.email || "",
          fecha_creacion: cot.fecha_creacion || cot.fecha || null, abonos: cot.abonos || [],
          garantia: cot.garantia || "", fechaGarantia: cot.fechaGarantia || "",
          garantiaRecibida: cot.garantiaRecibida || false, estado: cot.estado || "",
          numero: cot.numero || "", esEdicion: true, idOriginal: cot.id,
        },
        tipo: "cotizacion",
      },
    });
  };

  const manejarPDFCotizacion = async (cot) => {
    const doc = { ...cot, nombre_cliente: cot.clientes?.nombre || "No disponible", identificacion: cot.clientes?.identificacion || "-", telefono: cot.clientes?.telefono || "-", direccion: cot.clientes?.direccion || "-", email: cot.clientes?.email || "-" };
    await generarPDF(doc, "cotizacion");
  };

  // ════════════════════════════════════════
  // CITAS — CRUD
  // ════════════════════════════════════════
  const abrirModalCita = (cita = null) => {
    if (cita) {
      setCitaEditarId(cita.id);
      setFormCita({ cliente_id: cita.cliente_id, hora: cita.hora || "09:00", estado: cita.estado || "programada", motivo: cita.motivo || "", notas: cita.notas || "" });
      setClienteSelCita(cita.clientes || null);
      setBuscarClienteCita(cita.clientes?.nombre || "");
    } else {
      setCitaEditarId(null);
      setFormCita({ cliente_id: null, hora: "09:00", estado: "programada", motivo: "", notas: "" });
      setClienteSelCita(null);
      setBuscarClienteCita("");
    }
    setClientesSugerencias([]);
    setModalCita(true);
  };

  const cerrarModalCita = () => {
    setModalCita(false);
    setCitaEditarId(null);
    setClienteSelCita(null);
    setBuscarClienteCita("");
    setClientesSugerencias([]);
  };

  const guardarCita = async () => {
    if (!clienteSelCita) return Swal.fire("Cliente requerido", "Busca y selecciona un cliente antes de guardar.", "warning");
    if (!formCita.motivo.trim()) return Swal.fire("Motivo requerido", "Escribe el motivo de la cita.", "warning");

    const fecha = fechaSeleccionada.toISOString().split("T")[0];
    const datos = { fecha, hora: formCita.hora, estado: formCita.estado, motivo: formCita.motivo.trim(), notas: formCita.notas.trim(), cliente_id: clienteSelCita.id };

    const operacion = citaEditarId
      ? supabase.from("citas").update(datos).eq("id", citaEditarId)
      : supabase.from("citas").insert([datos]);

    const { error } = await operacion;
    if (!error) {
      Swal.fire({ icon: "success", title: citaEditarId ? "Cita actualizada" : "Cita agendada", timer: 1500, showConfirmButton: false });
      cerrarModalCita();
      cargarDatos();
      cargarEventosDelMes(fechaSeleccionada);
    } else {
      console.error("Error guardando cita:", error);
      Swal.fire("Error", "No se pudo guardar la cita.", "error");
    }
  };

  const eliminarCita = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar esta cita?", text: "Esta acción no se puede deshacer.", icon: "warning",
      showCancelButton: true, confirmButtonText: "Sí, eliminar", confirmButtonColor: "#ef4444", cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;
    const { error } = await supabase.from("citas").delete().eq("id", id);
    if (!error) {
      Swal.fire({ icon: "success", title: "Cita eliminada", timer: 1200, showConfirmButton: false });
      cargarDatos();
      cargarEventosDelMes(fechaSeleccionada);
    }
  };

  const cambiarEstadoCita = async (id, nuevoEstado) => {
    const { error } = await supabase.from("citas").update({ estado: nuevoEstado }).eq("id", id);
    if (!error) cargarDatos();
  };

  // ── WhatsApp ──
  const enviarWhatsApp = (cita) => {
    const cliente = cita.clientes || {};
    const tel = (cliente.telefono || "").replace(/\D/g, "");
    if (!tel) return Swal.fire("Sin teléfono", "Este cliente no tiene teléfono registrado.", "info");
    const telCompleto = tel.startsWith("57") ? tel : `57${tel}`;
    const fechaTexto = formatoFecha(cita.fecha);
    const mensaje = encodeURIComponent(
      `Hola ${cliente.nombre || ""}! 👋\n\nTe confirmamos tu cita el *${fechaTexto}* a las *${cita.hora}*.\n📋 Motivo: ${cita.motivo || "Reunión"}\n\n¡Te esperamos! 🤝`
    );
    window.open(`https://wa.me/${telCompleto}?text=${mensaje}`, "_blank");
  };

  // ── Generar cotización desde cita ──
  const generarCotizacionDesdeCita = (cita) => {
    const cliente = cita.clientes || {};
    navigate("/crear-documento", {
      state: {
        documento: {
          nombre_cliente: cliente.nombre || "", identificacion: cliente.identificacion || "",
          telefono: cliente.telefono || "", direccion: cliente.direccion || "", email: cliente.email || "",
          cliente_id: cliente.id,
        },
        tipo: "cotizacion",
        precargaCliente: true,
      },
    });
  };

  // ── Buscar clientes para el modal de citas ──
  useEffect(() => {
    if (buscandoRef.current) { buscandoRef.current = false; return; }
    if (buscarClienteCita.trim().length < 2) { setClientesSugerencias([]); return; }
    const timeout = setTimeout(async () => {
      const q = buscarClienteCita.trim().toLowerCase();
      const { data } = await supabase.from("clientes").select("id, nombre, telefono, direccion, email, identificacion").ilike("nombre", `%${q}%`).limit(6);
      setClientesSugerencias(data || []);
    }, 300);
    return () => clearTimeout(timeout);
  }, [buscarClienteCita]);

  const seleccionarClienteCita = (cliente) => {
    buscandoRef.current = true;
    setClienteSelCita(cliente);
    setFormCita({ ...formCita, cliente_id: cliente.id });
    setBuscarClienteCita(cliente.nombre);
    setClientesSugerencias([]);
  };

  // ── Mini-resumen del día ──
  const fechaStr = fechaSeleccionada.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const resumenDia = useMemo(() => {
    const items = [];
    if (ordenes.length > 0) items.push(`${ordenes.length} pedido${ordenes.length > 1 ? "s" : ""}`);
    if (cotizaciones.length > 0) items.push(`${cotizaciones.length} cotización${cotizaciones.length > 1 ? "es" : ""}`);
    if (citas.length > 0) items.push(`${citas.length} cita${citas.length > 1 ? "s" : ""}`);
    if (notas.length > 0) items.push(`${notas.length} nota${notas.length > 1 ? "s" : ""}`);
    return items.length > 0 ? items.join(" · ") : "Día libre";
  }, [ordenes.length, cotizaciones.length, citas.length, notas.length]);

  // ════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════
  return (
    <Protegido>
      <div className="sw-pagina">
        <div className="sw-pagina-contenido" style={{ maxWidth: 1000 }}>

          {/* ═══ HEADER ═══ */}
          <div className="sw-header">
            <h1 className="sw-header-titulo">📅 Calendario y Agenda</h1>
          </div>

          {/* ═══ MINI-RESUMEN DEL DÍA ═══ */}
          <div style={{
            padding: "10px 16px", marginBottom: 16, borderRadius: 10,
            background: "linear-gradient(135deg, #f0f9ff, #eff6ff)",
            border: "1px solid #bae6fd",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0369a1", textTransform: "capitalize" }}>
              📌 {fechaStr}
            </div>
            <div style={{ fontSize: 12, color: "#0c4a6e", fontWeight: 500 }}>
              {resumenDia}
            </div>
          </div>

          {/* ═══ FILA 1: CALENDARIO + NOTAS ═══ */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>

            {/* Calendario */}
            <div style={{ flex: "1.5", minWidth: 300 }}>
              <div className="sw-card">
                <div className="sw-card-body" style={{ padding: 12 }}>
                  <Calendar
                    onChange={handleFechaChange}
                    value={fechaSeleccionada}
                    className="react-calendar sw-calendario-moderno"
                    tileContent={tileContent}
                    onActiveStartDateChange={({ activeStartDate }) => cargarEventosDelMes(activeStartDate)}
                  />
                  {/* Leyenda */}
                  <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 10, fontSize: 11, color: "#6b7280", flexWrap: "wrap" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0077B6", display: "inline-block" }} /> Pedidos
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", display: "inline-block" }} /> Cotizaciones
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#8b5cf6", display: "inline-block" }} /> Citas
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notas */}
            <div style={{ flex: 1, minWidth: 280 }}>
              <div className="sw-card">
                <div className="sw-card-header">📝 Notas</div>
                <div className="sw-card-body">
                  <textarea
                    value={nuevaNota}
                    onChange={(e) => setNuevaNota(e.target.value)}
                    placeholder="Escribe una nota o recordatorio..."
                    rows={3}
                    style={{ width: "100%", marginBottom: 10, padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, resize: "vertical", boxSizing: "border-box" }}
                  />
                  <button className="sw-btn sw-btn-primario" style={{ width: "100%" }} onClick={guardarNota}>
                    💾 Guardar Nota
                  </button>
                  <div style={{ marginTop: 16 }}>
                    {notas.length === 0 && (
                      <div style={{ textAlign: "center", color: "#9ca3af", padding: 16, fontSize: 13 }}>No hay notas para este día.</div>
                    )}
                    {notas.map((nota) => (
                      <div key={nota.id} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        background: "#f8fafc", padding: "10px 12px", marginBottom: 6, borderRadius: 8,
                        border: "1px solid #f3f4f6", fontSize: 13,
                      }}>
                        <span>{nota.descripcion}</span>
                        <button onClick={() => borrarNota(nota.id)}
                          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4, borderRadius: 4, minHeight: "auto", color: "#ef4444" }}>
                          ❌
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ═══ FILA 2: DOCUMENTOS (TABS) + CITAS ═══ */}
          <div style={{ display: "flex", marginTop: 16, gap: 16, flexWrap: "wrap" }}>

            {/* ──────── DOCUMENTOS CON TABS ──────── */}
            <div style={{ flex: 1, minWidth: 300 }}>
              <div className="sw-card">
                {/* Tabs Header */}
                <div style={{
                  display: "flex", borderBottom: "1px solid #e5e7eb", overflow: "hidden",
                  borderTopLeftRadius: 12, borderTopRightRadius: 12,
                }}>
                  <button
                    onClick={() => setTabDocumentos("pedidos")}
                    style={{
                      flex: 1, padding: "12px 8px", border: "none", cursor: "pointer",
                      fontSize: 13, fontWeight: 600, transition: "all 0.2s",
                      background: tabDocumentos === "pedidos" ? "var(--sw-gradiente-primario, linear-gradient(135deg, #00B4D8, #0077B6))" : "white",
                      color: tabDocumentos === "pedidos" ? "white" : "#6b7280",
                      borderTopLeftRadius: 12,
                    }}
                  >
                    📦 Pedidos ({ordenes.length})
                  </button>
                  <button
                    onClick={() => setTabDocumentos("cotizaciones")}
                    style={{
                      flex: 1, padding: "12px 8px", border: "none", cursor: "pointer",
                      fontSize: 13, fontWeight: 600, transition: "all 0.2s",
                      background: tabDocumentos === "cotizaciones" ? "linear-gradient(135deg, #f59e0b, #d97706)" : "white",
                      color: tabDocumentos === "cotizaciones" ? "white" : "#6b7280",
                      borderTopRightRadius: 12,
                    }}
                  >
                    📋 Cotizaciones ({cotizaciones.length})
                  </button>
                </div>

                {/* Tab Content */}
                <div className="sw-card-body" style={{ padding: 0, minHeight: 120 }}>
                  {tabDocumentos === "pedidos" ? (
                    /* ── PEDIDOS ── */
                    ordenes.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, padding: 24 }}>No hay pedidos para este día.</div>
                    ) : ordenes.map((orden) => (
                      <div key={orden.id} style={{
                        padding: "12px 16px", borderBottom: "1px solid #f3f4f6",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        transition: "background 0.15s",
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f0f9ff"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: "#0077B6", fontSize: 14 }}>{orden.numero || "OP-???"}</div>
                          <div style={{ fontSize: 13, color: "#111827" }}>{orden.clientes?.nombre || "Cliente"}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{money(orden.total_neto || orden.total)}</div>
                          {(orden.fecha_entrega || orden.fecha_devolucion) && (
                            <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>
                              {orden.fecha_entrega && `📦 ${formatoFecha(orden.fecha_entrega)}`}
                              {orden.fecha_entrega && orden.fecha_devolucion && "  ·  "}
                              {orden.fecha_devolucion && `📥 ${formatoFecha(orden.fecha_devolucion)}`}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <IconoPago orden={orden} />
                          <button onClick={() => editarOrden(orden)} title="Editar" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4, minHeight: "auto" }}>✏️</button>
                          <button onClick={() => manejarPDF(orden)} title="PDF" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4, minHeight: "auto" }}>📄</button>
                          <button onClick={() => manejarRemision(orden)} title="Remisión" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4, minHeight: "auto" }}>🚚</button>
                        </div>
                      </div>
                    ))
                  ) : (
                    /* ── COTIZACIONES ── */
                    cotizaciones.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, padding: 24 }}>No hay cotizaciones para este día.</div>
                    ) : cotizaciones.map((cot) => (
                      <div key={cot.id} style={{
                        padding: "12px 16px", borderBottom: "1px solid #f3f4f6",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        transition: "background 0.15s",
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#fffbeb"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: "#b45309", fontSize: 14 }}>{cot.numero || "COT-???"}</div>
                          <div style={{ fontSize: 13, color: "#111827" }}>{cot.clientes?.nombre || "Cliente"}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{money(cot.total_neto || cot.total)}</div>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => editarCotizacion(cot)} title="Editar" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4, minHeight: "auto" }}>✏️</button>
                          <button onClick={() => manejarPDFCotizacion(cot)} title="PDF" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4, minHeight: "auto" }}>📄</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* ──────── CITAS DEL DÍA ──────── */}
            <div style={{ flex: 1, minWidth: 300 }}>
              <div className="sw-card">
                <div className="sw-card-header" style={{ background: "linear-gradient(135deg, #8b5cf6, #7c3aed)", color: "white", borderBottom: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>🤝 Citas del Día ({citas.length})</span>
                  <button
                    onClick={() => abrirModalCita()}
                    style={{
                      padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.4)",
                      background: "rgba(255,255,255,0.15)", color: "white", cursor: "pointer",
                      fontSize: 12, fontWeight: 600, transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.3)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
                  >
                    ＋ Nueva
                  </button>
                </div>
                <div className="sw-card-body" style={{ padding: 0, minHeight: 120 }}>
                  {citas.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 24 }}>
                      <div style={{ fontSize: 32, marginBottom: 6 }}>🤝</div>
                      <div style={{ fontSize: 13, color: "#9ca3af" }}>No hay citas para este día.</div>
                      <button
                        onClick={() => abrirModalCita()}
                        style={{
                          marginTop: 10, padding: "8px 16px", borderRadius: 8, border: "none",
                          background: "linear-gradient(135deg, #8b5cf6, #7c3aed)", color: "white",
                          cursor: "pointer", fontSize: 12, fontWeight: 600, transition: "all 0.2s",
                        }}
                      >
                        Agendar primera cita
                      </button>
                    </div>
                  ) : (
                    citas.map((cita) => {
                      const est = ESTADOS_CITA[cita.estado] || ESTADOS_CITA.programada;
                      const cliente = cita.clientes || {};
                      const tel = (cliente.telefono || "").replace(/\D/g, "");
                      return (
                        <div key={cita.id} style={{
                          padding: "12px 14px", borderBottom: "1px solid #f3f4f6",
                          borderLeft: `3px solid ${est.color}`, transition: "background 0.15s",
                        }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#faf5ff"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >
                          {/* Fila superior: hora + estado + acciones */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 15, fontWeight: 700, color: "#7c3aed" }}>🕐 {cita.hora?.slice(0, 5)}</span>
                              {/* Badge estado */}
                              <span style={{
                                fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                                background: est.bg, color: est.color, border: `1px solid ${est.border}`,
                              }}>
                                {est.emoji} {est.label}
                              </span>
                            </div>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button onClick={() => abrirModalCita(cita)} title="Editar" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, padding: 3, minHeight: "auto" }}>✏️</button>
                              <button onClick={() => eliminarCita(cita.id)} title="Eliminar" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, padding: 3, minHeight: "auto", color: "#ef4444" }}>🗑️</button>
                            </div>
                          </div>

                          {/* Cliente + motivo */}
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{cliente.nombre || "Sin cliente"}</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{cita.motivo}</div>
                          {cita.notas && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2, fontStyle: "italic" }}>💬 {cita.notas}</div>}

                          {/* Botones de acción */}
                          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                            {/* Cambio rápido de estado */}
                            {cita.estado === "programada" && (
                              <button onClick={() => cambiarEstadoCita(cita.id, "confirmada")}
                                style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #a7f3d0", background: "#ecfdf5", color: "#059669", fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>
                                ✓ Confirmar
                              </button>
                            )}
                            {cita.estado === "confirmada" && (
                              <button onClick={() => cambiarEstadoCita(cita.id, "completada")}
                                style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>
                                ✓ Completar
                              </button>
                            )}
                            {(cita.estado === "programada" || cita.estado === "confirmada") && (
                              <button onClick={() => cambiarEstadoCita(cita.id, "no_asistio")}
                                style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>
                                ✕ No asistió
                              </button>
                            )}
                            {/* WhatsApp */}
                            {tel && (
                              <button onClick={() => enviarWhatsApp(cita)}
                                style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#16a34a", fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>
                                💬 WhatsApp
                              </button>
                            )}
                            {/* Generar cotización */}
                            <button onClick={() => generarCotizacionDesdeCita(cita)}
                              style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #fde68a", background: "#fefce8", color: "#b45309", fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>
                              📋 Cotizar
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════
              MODAL: CREAR / EDITAR CITA
          ═══════════════════════════════════════ */}
          {modalCita && (
            <div style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(0,0,0,0.4)", zIndex: 9999,
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 16,
            }}
              onClick={(e) => { if (e.target === e.currentTarget) cerrarModalCita(); }}
            >
              <div style={{
                background: "white", borderRadius: 14, width: "100%", maxWidth: 480,
                maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              }}>
                {/* Header modal */}
                <div style={{
                  padding: "16px 20px", borderBottom: "1px solid #f3f4f6",
                  background: "linear-gradient(135deg, #8b5cf6, #7c3aed)", borderRadius: "14px 14px 0 0",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <h3 style={{ margin: 0, color: "white", fontSize: 16, fontWeight: 700 }}>
                    {citaEditarId ? "✏️ Editar Cita" : "🤝 Nueva Cita"}
                  </h3>
                  <button onClick={cerrarModalCita}
                    style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "white", fontSize: 18, cursor: "pointer", borderRadius: 6, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    ✕
                  </button>
                </div>

                {/* Body modal */}
                <div style={{ padding: "20px" }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16, textAlign: "center", fontWeight: 500 }}>
                    📅 {fechaStr}
                  </div>

                  {/* Buscar cliente */}
                  <div style={{ marginBottom: 14, position: "relative" }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#4b5563", marginBottom: 4, display: "block" }}>Cliente *</label>
                    <input
                      type="text"
                      placeholder="Buscar cliente por nombre..."
                      value={buscarClienteCita}
                      onChange={(e) => { setClienteSelCita(null); setFormCita({ ...formCita, cliente_id: null }); setBuscarClienteCita(e.target.value); }}
                      style={{ width: "100%", padding: "10px 12px", border: `1px solid ${clienteSelCita ? "#a7f3d0" : "#e5e7eb"}`, borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: clienteSelCita ? "#f0fdf4" : "white" }}
                    />
                    {clienteSelCita && (
                      <div style={{ fontSize: 11, color: "#059669", marginTop: 4, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <span>✅ {clienteSelCita.nombre}</span>
                        {clienteSelCita.telefono && <span>📱 {clienteSelCita.telefono}</span>}
                      </div>
                    )}
                    {/* Sugerencias */}
                    {clientesSugerencias.length > 0 && !clienteSelCita && (
                      <div style={{
                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
                        background: "white", border: "1px solid #e5e7eb", borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)", maxHeight: 180, overflowY: "auto",
                      }}>
                        {clientesSugerencias.map((c) => (
                          <div key={c.id}
                            onClick={() => seleccionarClienteCita(c)}
                            style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f3f4f6", transition: "background 0.1s" }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#f0f9ff"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                          >
                            <div style={{ fontWeight: 600 }}>{c.nombre}</div>
                            {c.telefono && <div style={{ fontSize: 11, color: "#9ca3af" }}>📱 {c.telefono}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Hora + Estado */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#4b5563", marginBottom: 4, display: "block" }}>Hora *</label>
                      <input type="time" value={formCita.hora}
                        onChange={(e) => setFormCita({ ...formCita, hora: e.target.value })}
                        style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#4b5563", marginBottom: 4, display: "block" }}>Estado</label>
                      <select value={formCita.estado}
                        onChange={(e) => setFormCita({ ...formCita, estado: e.target.value })}
                        style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "white" }}
                      >
                        {Object.entries(ESTADOS_CITA).map(([key, val]) => (
                          <option key={key} value={key}>{val.emoji} {val.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Motivo */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#4b5563", marginBottom: 4, display: "block" }}>Motivo *</label>
                    <input type="text" placeholder="Ej: Revisar mobiliario, Planificación de evento..."
                      value={formCita.motivo}
                      onChange={(e) => setFormCita({ ...formCita, motivo: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>

                  {/* Notas */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#4b5563", marginBottom: 4, display: "block" }}>Notas (opcional)</label>
                    <textarea
                      placeholder="Observaciones adicionales..."
                      value={formCita.notas}
                      onChange={(e) => setFormCita({ ...formCita, notas: e.target.value })}
                      rows={2}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, resize: "vertical", boxSizing: "border-box" }}
                    />
                  </div>

                  {/* Botones */}
                  <div style={{ display: "flex", gap: 10 }}>
                    <button className="sw-btn sw-btn-primario" style={{ flex: 1, background: "linear-gradient(135deg, #8b5cf6, #7c3aed)" }} onClick={guardarCita}>
                      {citaEditarId ? "💾 Actualizar" : "🤝 Agendar Cita"}
                    </button>
                    <button className="sw-btn sw-btn-secundario" style={{ flex: 1 }} onClick={cerrarModalCita}>
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </Protegido>
  );
}